// @ts-nocheck

// App
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';
import type { RootReduxState } from 'app/shared/flux/reducers';

// Actions
import AppActions from 'app/shared/flux/actions/AppActions';

// Icons
import IconHud from 'images/icons/hud.min.svg';

// Components
import FooterLinks from './FooterLinks';
import AppBadges from './AppBadges';
import Container from 'app/shared/core/Container';
import Center from 'app/shared/modules/Center';
import Section from 'app/shared/modules/Section';
import CrossbrandFooter from 'app/shared/modules/navigation/footer/CrossbrandFooter';
import Text from 'app/shared/core/Text';

// Misc / utils
import PATHS_MANIFEST from 'app/shared/utils/pathsManifest';
import * as S from './styles';

type FooterProps = {
  noMarginTop?: boolean;
  crossbrand?: boolean;
  hideFooter?: boolean;
};

const Footer = ({ noMarginTop = false, crossbrand = false, hideFooter = false }: FooterProps = {}) => {
  // Redux
  const dispatch = useDispatch();
  const isMobile = useSelector((state: RootReduxState) => state.app.device.isMobile);

  // Hooks must be called before any early returns
  const yieldTrackAndTransitionTo = useYieldCallback((page, path) => {
    window.router.transitionTo(path);
  });

  const yieldTrackAndOpenModal = useYieldCallback(() => {
    dispatch(AppActions.showPopupModal('PromptFeedback'));
  });

  // Early return if footer should be hidden
  if (hideFooter) {
    return null;
  }

  const handleClick = (e, { path, isExternalUrl = false }) => {
    const page = e && e.currentTarget.getAttribute('data-footer-page');

    if (!isExternalUrl) {
      // Prevent the browser from going through with its default <a> behavior
      e.preventDefault();

      yieldTrackAndTransitionTo(page, path);
    }
  };

  const handleModal = (e) => {
    const page = e && e.currentTarget.getAttribute('data-footer-page');

    if (page === 'feedback') {
      yieldTrackAndOpenModal();
    }
  };

  // Common footer content
  const footerContent = (
    <>
      {crossbrand && (
        <S.CrossbrandNav aria-label="Zillow Group brands">
          <CrossbrandFooter />
        </S.CrossbrandNav>
      )}
      <FooterLinks onLinkClick={handleClick} onModalLinkClick={handleModal} />
      <S.AppBadgeSection>
        <AppBadges onBadgeClick={handleClick} />
      </S.AppBadgeSection>
      <S.TextSection>
        <S.FooterText size="tiny">
          Zillow Group is committed to ensuring digital accessibility for individuals with disabilities. We are
          continuously working to improve the accessibility of our web experience for everyone, and welcome feedback and
          accommodation requests. If you wish to report an issue or seek an accommodation please contact us at{' '}
          <S.Linker to="mailto:support@hotpads.com">support@hotpads.com</S.Linker>
        </S.FooterText>
      </S.TextSection>
      <S.FairHousingTextSection>
        <S.Linker linkType="inherit" to={PATHS_MANIFEST.hud} target="_blank" rel="noopener">
          <S.FooterText size="tiny">
            Fair Housing and Equal Opportunity
            <S.HudImg
              loading="lazy"
              src={IconHud}
              height="16px"
              width="16px"
              alt="United States Department of Housing and Urban Development badge"
            />
          </S.FooterText>
        </S.Linker>
      </S.FairHousingTextSection>
      <S.FairHousingSection>
        <S.FooterText size="tiny">
          <S.Linker linkType="inherit" to={PATHS_MANIFEST.fairHousingGuide} target="_blank" rel="noopener">
            Fair Housing Guide
          </S.Linker>
        </S.FooterText>
      </S.FairHousingSection>
      <S.HotPadsSection>
        <S.FooterText size="tiny">© {new Date().getFullYear()} HotPads</S.FooterText>
      </S.HotPadsSection>
    </>
  );

  // Mobile layout - use existing styling
  if (isMobile) {
    return <S.Footer noMarginTop={noMarginTop}>{footerContent}</S.Footer>;
  }

  // Desktop layout - use Container/Section/Center hierarchy
  return (
    <S.Footer noMarginTop={noMarginTop}>
      <Container>
        <Section>
          <Center>{footerContent}</Center>
        </Section>
      </Container>
    </S.Footer>
  );
};

export default Footer;
